import * as React from 'react';
import { Editor } from '@tinymce/tinymce-react';
import { TINY_MCE_API_KEY } from 'core/constants';
import { Radio } from '@teamsnap/snap-ui';
import { SignatureType, Waiver } from './types';
import { Button, Drawer, Panel, PanelBody, PanelFooter, PanelHeader, PanelRow, Field } from '@teamsnap/teamsnap-ui';
import FileUpload from 'components/common/file-upload';
import { handleWaiverAttachmentUploadRequest } from './serverRequests';
import { useMutation } from '@apollo/client';
import * as CreateWaiver from 'core/gql/mutations/create-waiver';
import * as UpdateWaiver from 'core/gql/mutations/update-waiver';

import './NewWaiver.scss';

// set editor style to match waiver display style in registration SignWaviers.scss
const EDITOR_STYLE =
  'body { font-family: "Lato", "Open Sans", Helvetica, sans-serif; } ' +
  'p, h1, h2, h3, h4, h5, h6, pre, ul, ol { margin: 0; } ' +
  'ol ol { list-style-type: lower-alpha; } ' +
  'ol ol ol { list-style-type: lower-roman; } ' +
  'ol ol ol ol { list-style-type: lower-alpha; } ' +
  'ol, ul { padding-left: 2em; } ';

type Props = {
  formId: number;
  open: boolean;
  closeFn: () => void;
  waiver: Waiver;
  onSaveWaiver: (w: Waiver) => void;
};

const NewWaiver = ({ formId, waiver, onSaveWaiver, open, closeFn }: Props) => {
  const [title, setTitle] = React.useState(waiver?.title || '');
  const [content, setContent] = React.useState(waiver?.content || '');
  const [signatureType, setSignatureType] = React.useState<SignatureType>(waiver?.signatureType || 'checkbox_accept');
  const [attachment, setAttachment] = React.useState<File>();
  const [fileError, setFileError] = React.useState('');

  const [createWaiver] = useMutation(CreateWaiver.MUTATION);
  const [updateWaiver] = useMutation(UpdateWaiver.MUTATION);

  React.useEffect(() => {
    if (waiver) {
      setTitle(waiver.title);
      setContent(waiver.content);
      setSignatureType(waiver.signatureType);

      if (waiver.attachment || waiver.filename) {
        setAttachment(waiver.attachment || new File([], waiver.filename));
      }
    }
  }, [waiver]);

  const onClose = (shouldSave: boolean) => {
    setAttachment(null);
    if (shouldSave) {
      onSaveWaiver({
        title,
        content,
        attachment,
        filename: attachment?.name,
        signatureType,
        id: waiver?.id || null,
        uuid: waiver?.uuid || null,
        active: waiver?.active || true,
      });
    }

    setFileError('');
    setTitle('');
    setContent('');
    setAttachment(undefined);
    setSignatureType('checkbox_accept');
    closeFn();
  };

  const getRadioProps = (option: SignatureType) => ({
    input: { checked: option === signatureType, onChange: (x) => setSignatureType(x.target.value as SignatureType) },
  });

  const onWaiverSave = async () => {
    let filename;
    if (attachment && attachment.size > 0) {
      try {
        const file = await handleWaiverAttachmentUploadRequest(formId, title, attachment);
        filename = file.data.file_name;
      } catch (e) {
        setFileError(e);
        return;
      }
    }

    const variables = {
      ...waiver,
      formId,
      title,
      content,
      signatureType,
      filename: filename ?? null,
      originalFileName: attachment ? attachment.name : null,
    };

    try {
      await (waiver ? updateWaiver({ variables }) : createWaiver({ variables }));
    } catch (e) {
      console.log(e);
    }

    onClose(true);
  };

  return (
    <Drawer
      open={open}
      allowOverlayClose={false}
      closeFn={() => onClose(false)}
      style={{ maxWidth: '730px' }}
      mods="u-sizeFull"
      showOverlay
    >
      <Panel mods="Panel NewWaiver" className="u-padSidesXl">
        <PanelHeader>
          <h3 data-testid="create-agreement-heading">Create Digital Agreement</h3>
        </PanelHeader>

        <PanelBody>
          <PanelRow>
            <h3 className="u-spaceTopSm u-textBold" data-testid="create-agreement-title">
              Agreement Title<span className="sui-text-red-40">*</span>
            </h3>
            <Field
              type="text"
              mods="u-size1of1"
              name={'waiver-title'}
              formFieldProps={{
                placeholder: 'e.g., Code of Conduct',
                size: 'large',
                inputProps: {
                  'data-testid': 'NewWaiver--title',
                  value: title,
                  onChange: (e) => setTitle(e.target.value),
                  style: {
                    padding: 16,
                  },
                },
              }}
            />
          </PanelRow>

          <h3 className="u-spaceTopSm sui-font-bold" data-testid="create-agreement-content">
            Digital Agreement Content<span className="sui-text-red-40">*</span>
          </h3>
          <PanelRow className="Panel-row Panel-row-waiver-text">
            <Editor
              id="waiver-text"
              apiKey={TINY_MCE_API_KEY}
              onEditorChange={setContent}
              value={content}
              init={{
                menubar: false,
                resize: false,
                preview_styles: false,
                relative_urls: false,
                convert_urls: false,
                height: '100%',
                statusbar: false,
                plugins: 'link lists textpattern',
                contextmenu: 'list link',
                toolbar_mode: 'wrap',
                toolbar:
                  'undo redo | formatselect | bold italic underline forecolor backcolor | link unlink | alignleft aligncenter alignright alignjustify | numlist bullist',
                placeholder: 'e.g. You must abide by our code of conduct to participate...',
                content_style: EDITOR_STYLE,
              }}
            />
          </PanelRow>
          <FileUpload filename={waiver?.filename} originalFileName={waiver?.originalFileName} file={attachment} setFile={setAttachment} error={fileError} setError={setFileError} />
          <PanelRow>
            <h3 className="sui-font-bold sui-mt-1" data-testid="signature-type-label">
              Signature Type<span className="sui-text-red-40">*</span>
            </h3>
            <span className="sui-mb-1">Signature will be collected per registration.</span>
            <Radio
              label="One Click Accept"
              data-testid="signature-one-click-accept-radio"
              name="signature-type"
              value="checkbox_accept"
              extraProps={getRadioProps('checkbox_accept')}
            />
            <Radio
              label="Registrant Initials"
              data-testid="signature-initials-radio"
              name="signature-type"
              value="initials"
              extraProps={getRadioProps('initials')}
            />
            <Radio
              label="Registrant Full Name"
              data-testid="signature-full-name-radio"
              name="signature-type"
              value="full_name"
              extraProps={getRadioProps('full_name')}
            />
          </PanelRow>
        </PanelBody>

        <PanelFooter>
          <Button
            style={{
              backgroundColor: '#f6f8fb',
              marginRight: 16,
            }}
            onClick={() => onClose(false)}
            size="large"
            mods="u-padSidesXl"
            testId="cancel-waiver-button"
          >
            Cancel
          </Button>
          <Button
            style={{}}
            onClick={() => onWaiverSave()}
            color="primary"
            size="large"
            mods="u-padSidesXxl"
            isDisabled={!(title && content)}
            testId="save-waiver-button"
          >
            Save
          </Button>
        </PanelFooter>
      </Panel>
    </Drawer>
  );
};

export default NewWaiver;
